import React from "react";
import Select from "react-select";
import { useField, useFormikContext } from "formik";

const customStyles = (height = 44) => ({
    control: (provided, state) => ({
        ...provided,
        minHeight: height,
        height: height,
        fontSize: '0.875rem',
        boxShadow: state.isFocused ? '0 0 0 2px rgba(37, 99, 235, 0.1)' : provided.boxShadow,
        borderColor: state.isFocused ? '#2563eb' : provided.borderColor,
        borderRadius: '8px',
        transition: 'all 0.2s ease',
        '&:hover': {
            borderColor: '#9ca3af'
        }
    }),
    valueContainer: (provided) => ({
        ...provided,
        height: height,
        padding: '0 12px',
    }),
    input: (provided) => ({
        ...provided,
        margin: 0,
        padding: 0,
    }),
    indicatorsContainer: (provided) => ({
        ...provided,
        height: height,
    }),
    menuPortal: (provided) => ({
        ...provided,
        zIndex: 9999,
    }),
    menu: (provided) => ({
        ...provided,
        borderRadius: '8px',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        border: '1px solid #e5e7eb'
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected ? '#2563eb' : state.isFocused ? '#f3f4f6' : 'white',
        color: state.isSelected ? 'white' : '#374151',
        '&:active': {
            backgroundColor: '#1d4ed8'
        }
    })
});

const FormikDropdown = ({
    label,
    name,
    options,
    placeholder = "Select",
    required = false,
    needSearch = false,
    height = 44, // default height in px
    ...props
}) => {
    const { setFieldValue } = useFormikContext();
    const [field, meta] = useField(name);

    const handleChange = (selectedOption) => {
        setFieldValue(name, selectedOption ? selectedOption.value : "");
        if (props.onChange) {
            props.onChange(selectedOption); // Call the parent onChange if provided
        }
    };

    return (
        <div className="form-control w-full">
            {/* Label */}
            {label && (
                <label
                    htmlFor={props.id || props.name}
                    className="block text-sm font-medium text-gray-700 mb-1"
                >
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}
            <Select
                options={options}
                value={options.find((option) => option.value === field.value) || null}
                onChange={handleChange}
                classNamePrefix="react-select"
                placeholder={placeholder}
                isSearchable={needSearch}
                styles={customStyles(height)}
                menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                menuPosition="fixed"
                menuShouldBlockScroll={true}
                menuShouldScrollIntoView={true}
            />

            {/* Error Message */}
            {meta.touched && meta.error && (
                <p 
                    id={`${name}-error`}
                    className="mt-1 text-sm text-red-600 flex items-center gap-1"
                    role="alert"
                >
                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {meta.error}
                </p>
            )}
        </div>
    );
};

export default FormikDropdown;
